import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowUp,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  deleteAppointment,
  getAppointments,
  getProducts,
  updateAppointmentStatus,
} from '../lib/api';
import { Appointment, AppointmentStatus, Product } from '../types';
import { useConfirm, useToast } from '../ui/UIContext';
import { AppointmentDetailDrawer } from '../components/AppointmentDetailDrawer';
import { AdminTableSkeleton } from '../components/AdminSkeletons';

const STATUS_FILTERS: Array<AppointmentStatus | 'ALL'> = [
  'ALL',
  'PENDING',
  'CONFIRMED',
  'COMPLETED',
  'CANCELLED',
];

const STATUS_BADGE: Record<AppointmentStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-200 text-gray-700',
};

type SortKey = 'createdAt' | 'appointmentDate' | 'name';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 10;

export function AdminAppointmentsPage() {
  const { t } = useTranslation();
  const showToast = useToast();
  const confirm = useConfirm();

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [appts, prods] = await Promise.all([getAppointments(), getProducts()]);
        setAppointments(appts);
        setProducts(prods);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load admin data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const productMap = useMemo(() => {
    const map = new Map<string, Product>();
    products.forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  const counts = useMemo(() => {
    const acc: Record<string, number> = { ALL: appointments.length };
    appointments.forEach((a) => {
      acc[a.status] = (acc[a.status] ?? 0) + 1;
    });
    return acc;
  }, [appointments]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = appointments;
    if (statusFilter !== 'ALL') list = list.filter((a) => a.status === statusFilter);
    if (term) {
      list = list.filter(
        (a) =>
          a.name.toLowerCase().includes(term) ||
          a.email.toLowerCase().includes(term) ||
          a.phone.toLowerCase().includes(term),
      );
    }
    if (from) list = list.filter((a) => a.appointmentDate >= from);
    if (to) list = list.filter((a) => a.appointmentDate <= to);

    const sorted = [...list].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [appointments, statusFilter, search, from, to, sortKey, sortDir]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, from, to, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  function clearFilters() {
    setStatusFilter('ALL');
    setSearch('');
    setFrom('');
    setTo('');
  }

  async function handleStatus(id: string, status: AppointmentStatus) {
    setBusyId(id);
    try {
      const updated = await updateAppointmentStatus(id, status);
      setAppointments((prev) => prev.map((a) => (a.id === id ? updated : a)));
      showToast(t('admin.toast.statusUpdated'), 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to update', 'error');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(id: string) {
    const ok = await confirm({
      title: t('admin.confirmDelete.title'),
      message: t('admin.confirmDelete.message'),
      destructive: true,
      confirmLabel: t('admin.actions.delete'),
    });
    if (!ok) return;
    setBusyId(id);
    try {
      await deleteAppointment(id);
      setAppointments((prev) => prev.filter((a) => a.id !== id));
      if (selectedId === id) setSelectedId(null);
      showToast(t('admin.toast.deleted'), 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to delete', 'error');
    } finally {
      setBusyId(null);
    }
  }

  const selected = selectedId ? appointments.find((a) => a.id === selectedId) ?? null : null;

  if (loading) {
    return <AdminTableSkeleton rows={8} columns={6} />;
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  const sortIcon = (key: SortKey) =>
    sortKey === key ? (
      sortDir === 'asc' ? (
        <ArrowUp className="w-3 h-3 inline" />
      ) : (
        <ArrowDown className="w-3 h-3 inline" />
      )
    ) : null;

  return (
    <section>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-ink">{t('admin.title')}</h1>
        <p className="text-ink-soft mt-1">{t('admin.subtitle')}</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((status) => {
            const active = statusFilter === status;
            const count = counts[status] ?? 0;
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  active
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {t(`admin.filter.${status.toLowerCase()}`)} ({count})
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('admin.search.label')}
            </label>
            <div className="relative">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('admin.search.placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full ps-9 pe-3 py-2 rounded-md border border-gray-300 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div>
            <label htmlFor="appt-from" className="block text-xs font-medium text-gray-600 mb-1">
              {t('admin.search.from')}
            </label>
            <input
              id="appt-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-3 py-2 rounded-md border border-gray-300 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="appt-to" className="block text-xs font-medium text-gray-600 mb-1">
              {t('admin.search.to')}
            </label>
            <input
              id="appt-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-3 py-2 rounded-md border border-gray-300 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {(search || from || to || statusFilter !== 'ALL') && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-100 border border-gray-200"
            >
              {t('admin.search.clear')}
            </button>
          )}
        </div>
      </div>

      {selected && (
        <AppointmentDetailDrawer
          appointment={selected}
          product={productMap.get(selected.productId) ?? null}
          busy={busyId === selected.id}
          onClose={() => setSelectedId(null)}
          onStatusChange={(status) => handleStatus(selected.id, status)}
          onDelete={() => handleDelete(selected.id)}
          onUpdated={(updated) =>
            setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
          }
        />
      )}

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg p-12 text-center text-gray-500 border border-gray-200">
          {t('admin.empty')}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      onClick={() => toggleSort('name')}
                      className="px-4 py-3 text-start text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      {t('admin.table.client')} {sortIcon('name')}
                    </th>
                    <th className="px-4 py-3 text-start text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.table.product')}
                    </th>
                    <th
                      onClick={() => toggleSort('appointmentDate')}
                      className="px-4 py-3 text-start text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      {t('admin.table.date')} {sortIcon('appointmentDate')}
                    </th>
                    <th
                      onClick={() => toggleSort('createdAt')}
                      className="px-4 py-3 text-start text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      {t('admin.table.requested')} {sortIcon('createdAt')}
                    </th>
                    <th className="px-4 py-3 text-start text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.table.status')}
                    </th>
                    <th className="px-4 py-3 text-end text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('admin.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pageItems.map((appt) => {
                    const product = productMap.get(appt.productId);
                    const busy = busyId === appt.id;
                    return (
                      <tr
                        key={appt.id}
                        onClick={() => setSelectedId(appt.id)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{appt.name}</div>
                          <div className="text-sm text-gray-500">{appt.email}</div>
                          <div className="text-sm text-gray-500">{appt.phone}</div>
                          {appt.notes && (
                            <div className="text-xs text-gray-400 mt-1 max-w-xs truncate">
                              {appt.notes}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {product?.title ?? appt.productId}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div>{appt.appointmentDate}</div>
                          <div className="text-xs text-gray-500">
                            {t(`form.timeSlots.${appt.timeSlot}`)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {appt.createdAt.slice(0, 10)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${STATUS_BADGE[appt.status]}`}
                          >
                            {t(`admin.status.${appt.status.toLowerCase()}`)}
                          </span>
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {appt.status !== 'CONFIRMED' && appt.status !== 'COMPLETED' && (
                              <button
                                onClick={() => handleStatus(appt.id, 'CONFIRMED')}
                                disabled={busy}
                                title={t('admin.actions.confirm')}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            {appt.status === 'CONFIRMED' && (
                              <button
                                onClick={() => handleStatus(appt.id, 'COMPLETED')}
                                disabled={busy}
                                title={t('admin.actions.complete')}
                                className="p-2 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                            )}
                            {appt.status !== 'PENDING' &&
                              appt.status !== 'CANCELLED' &&
                              appt.status !== 'COMPLETED' && (
                                <button
                                  onClick={() => handleStatus(appt.id, 'PENDING')}
                                  disabled={busy}
                                  title={t('admin.actions.reopen')}
                                  className="p-2 text-amber-600 hover:bg-amber-50 rounded disabled:opacity-50"
                                >
                                  <Clock className="w-4 h-4" />
                                </button>
                              )}
                            {appt.status !== 'CANCELLED' && (
                              <button
                                onClick={() => handleStatus(appt.id, 'CANCELLED')}
                                disabled={busy}
                                title={t('admin.actions.cancel')}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(appt.id)}
                              disabled={busy}
                              title={t('admin.actions.delete')}
                              className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-ink-soft">
              <div>
                {t('admin.pagination.showing', {
                  from: pageStart + 1,
                  to: Math.min(pageStart + PAGE_SIZE, filtered.length),
                  total: filtered.length,
                })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-2 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span>
                  {safePage} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-2 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-100"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
